import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { UserRole, User, UserPermissions, getUserPermissions } from '../types/User';

export type ViewMode = 'admin' | 'artist' | 'public' | 'strand' | 'judge';

export interface ViewConfig {
  showAdminActions: boolean;
  showFinancialDetails: boolean;
  showTechnicalRequirements: boolean;
  showContactInfo: boolean;
  showDemographics: boolean;
  showRecommenderInfo: boolean;
  showAvailability: boolean;
  showSocialMedia: boolean;
  showFullMedia: boolean;
  showInstrumentation: boolean;  // ✅ Fixed: capital 'I'
  showPublishedStrandCard: boolean;
}



export const VIEW_CONFIGS: Record<ViewMode, ViewConfig> = {
  public: {
    showAdminActions: false,
    showFinancialDetails: false,
    showTechnicalRequirements: false,
    showContactInfo: false,
    showDemographics: false,
    showRecommenderInfo: false,
    showAvailability: false,
    showSocialMedia: true,
    showFullMedia: true,
    showInstrumentation: true,
    showPublishedStrandCard: false
  },
  artist: {
    showAdminActions: false,
    showFinancialDetails: true,      // ✅ Artists should see their own financial info
    showTechnicalRequirements: true,  // ✅ Artists verify their tech specs
    showContactInfo: true,            // ✅ Artists verify contact details
    showDemographics: true,
    showRecommenderInfo: true,        // ✅ Artists see who submitted them
    showAvailability: true,           // ✅ Artists verify availability
    showSocialMedia: true,
    showFullMedia: true,
    showInstrumentation: true,
    showPublishedStrandCard: true     // ✅ Artists see where they're published
  },
  admin: {
    showAdminActions: true,
    showFinancialDetails: true,
    showTechnicalRequirements: true,
    showContactInfo: true,
    showDemographics: true,
    showRecommenderInfo: true,
    showAvailability: true,
    showSocialMedia: true,
    showFullMedia: true,
    showInstrumentation: true,
    showPublishedStrandCard: true
  },
  strand: {
    showAdminActions: false,
    showFinancialDetails: true,       // ✅ Programmers need to know fees
    showTechnicalRequirements: true,  // ✅ Programmers need tech specs
    showContactInfo: true,            // ✅ Programmers need to contact bands
    showDemographics: true,
    showRecommenderInfo: true,        // ✅ Programmers see booking contact
    showAvailability: true,           // ✅ Programmers need availability
    showSocialMedia: true,
    showFullMedia: true,
    showInstrumentation: true,
    showPublishedStrandCard: false
  },
  judge: {
    showAdminActions: false,
    showFinancialDetails: true,       // ✅ FIXED: Judges evaluate full package
    showTechnicalRequirements: true,  
    showContactInfo: true,            // ✅ FIXED: Judges may need to contact
    showDemographics: true,
    showRecommenderInfo: true,        
    showAvailability: true,           
    showSocialMedia: true,
    showFullMedia: true,
    showInstrumentation: true,
    showPublishedStrandCard: false
  }
};

interface StrandContext {
  strandSlug?: string;
  strandId?: string;
}

interface BandViewContextProps {
  viewMode: ViewMode;
  viewConfig: ViewConfig;
  currentUser: User | null;
  permissions: UserPermissions;
  strandContext?: StrandContext;
  actions: {
    handlePublish: () => void;
    handleEdit: () => void;
    handleDelete: () => void;
    handleAddToEvent: () => void;
  };
  setViewMode: (mode: ViewMode) => void;
}

const BandViewContext = createContext<BandViewContextProps | undefined>(undefined);

export const BandViewProvider: React.FC<{
  children: ReactNode;
  currentUser: User | null;
  initialViewMode?: ViewMode;
}> = ({ children, currentUser, initialViewMode = 'public' }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Derive initial mode: URL param wins, then prop, then default
  const urlView = searchParams.get('view') as ViewMode | null;
  const validModes: ViewMode[] = ['admin', 'artist', 'public', 'strand', 'judge'];
  const resolvedInitial = (urlView && validModes.includes(urlView)) ? urlView : initialViewMode;

  const [viewMode, setViewMode] = useState<ViewMode>(resolvedInitial);

  const { strandSlug, bandId } = useParams<{
    strandSlug?: string;
    bandId?: string;
  }>();

  const strandContext: StrandContext | undefined = strandSlug ? {
    strandSlug,
    strandId: bandId
  } : undefined;

  useEffect(() => {
    if (strandSlug) {
      setViewMode('strand');
    }
  }, [strandSlug]);

  // Keep URL in sync when view mode changes
  const setViewModeWithUrl = (mode: ViewMode) => {
    setViewMode(mode);
    const params = new URLSearchParams(searchParams);
    params.set('view', mode);
    navigate({ search: params.toString() }, { replace: true });
  };

  // Get permissions based on user role
  const permissions = currentUser 
    ? getUserPermissions(currentUser.role)
    : getUserPermissions(UserRole.Guest);

  // Get view configuration based on current view mode
  const viewConfig = VIEW_CONFIGS[viewMode];
  
  const actions = {
    handlePublish: () => {
      if (!viewConfig.showAdminActions) return;
      console.log('Publish action');
    },
    handleEdit: () => {
      if (!viewConfig.showAdminActions) return;
      console.log('Edit action');
    },
    handleDelete: () => {
      if (!viewConfig.showAdminActions) return;
      console.log('Delete action');
    },
    handleAddToEvent: () => {
      if (!viewConfig.showAdminActions) return;
      console.log('Add to event action');
    }
  };

  // Extended context value including strand context
  const contextValue: BandViewContextProps = {
    viewMode,
    viewConfig,
    currentUser,
    permissions,
    strandContext,
    actions,
    setViewMode: setViewModeWithUrl,
  };

  return (
    <BandViewContext.Provider value={contextValue}>
      {children}
    </BandViewContext.Provider>
  );
};

export const useBandView = () => {
  const context = useContext(BandViewContext);
  if (context === undefined) {
    throw new Error('useBandView must be used within a BandViewProvider');
  }
  return context;
};

export default BandViewContext;